import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { crumbHref } from './crumb-return';

/** True when the key event comes from a field where the user types. */
function isTypingTarget(target: EventTarget | null): boolean {
    if (!target || typeof (target as HTMLElement).tagName !== 'string') return false;
    const el = target as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    return el.isContentEditable === true || el.closest?.('[contenteditable]') != null;
}

/** True when the target is inside any dialog, such as the column filter panel. */
function isInsideDialog(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    return typeof el?.closest === 'function' && el.closest('[role="dialog"]') !== null;
}

/** The parts of a key event that the shortcut reads. */
export type ShortcutKeyEvent = Pick<
    KeyboardEvent,
    'key' | 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'defaultPrevented' | 'target'
>;

/** True when Alt+ArrowUp must go to the parent crumb for this event. */
export function shouldGoToParent(event: ShortcutKeyEvent, modalOpen: boolean): boolean {
    if (event.key !== 'ArrowUp' || !event.altKey) return false;
    if (event.ctrlKey || event.metaKey || event.shiftKey) return false;
    if (event.defaultPrevented || modalOpen) return false;
    if (isInsideDialog(event.target)) return false;
    return !isTypingTarget(event.target);
}

/**
 * Alt+ArrowUp goes to the parent crumb: the one step up the tree.
 * The shortcut does nothing in a text field, while a modal is open, or when
 * the trail has no parent crumb (`parentTo` is undefined).
 */
export function useParentCrumbShortcut(parentTo?: string): void {
    const navigate = useNavigate();
    useEffect(() => {
        if (!parentTo) return undefined;
        const onKeyDown = (event: KeyboardEvent) => {
            const modalOpen = document.querySelector('[role="dialog"][aria-modal="true"]') !== null;
            if (!shouldGoToParent(event, modalOpen)) return;
            event.preventDefault();
            navigate(crumbHref(parentTo));
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [parentTo, navigate]);
}
